import React from "react";
import styles from "../../../src/assets/scss/Billing/InvoiceDesign.module.scss";
import html2canvas from "html2canvas";
import { useLazyGetinvoicePdfQuery } from "../../slices/admin/admin";
import { BaseURL } from "../../lib/api";

const formatCurrency = (v) => {
    if (v == null || isNaN(v)) return "₹0.00";
    return (
        "₹" +
        Number(v).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
    );
};

const formatDate = (iso) => {
    const d = iso ? new Date(iso) : new Date();
    return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatTime = (iso) => {
    const d = iso ? new Date(iso) : new Date();
    return d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

const capitalize = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : "-";

const InvoicePrintDesign = ({ data = {}, mode = "thermal" }) => {
    console.log("data in invoice print", data);

    const order = data || {};
    const store = order.creatorStore || {}; // ✅ corrected
    const user = order.createdBy || {};
    const items = order.items || [];

    // ✅ compute total qty (productQuantity + productCount)
    const totalItems = items.length;
    const totalQuantity = items.reduce(
        (s, it) => s + (it.productQuantity || 0) + (it.productCount || 0),
        0
    );

    const handlePrint = async () => {
        const invoiceElement = document.getElementById("print-area");
        if (!invoiceElement) return;

        const buttons = invoiceElement.querySelectorAll(".addBtn, .noPrint");
        buttons.forEach((btn) => (btn.style.display = "none"));

        const canvas = await html2canvas(invoiceElement, { scale: 2 });
        const imgData = canvas.toDataURL("image/png");

        buttons.forEach((btn) => (btn.style.display = ""));

        const printWindow = window.open("", "_blank");
        printWindow.document.write(`
      <html>
        <head>
          <title>Invoice</title>
          <style>
            body, html { margin:0; padding:0; background:#fff; }
            img { max-width:100%; height:auto; display:block; margin:auto; }
          </style>
        </head>
        <body>
          <img src="${imgData}" />
          <script>
            window.onload = function() { window.print(); window.onafterprint = function(){ window.close(); } }
          </script>
        </body>
      </html>
    `);
        printWindow.document.close();
    };

    const [triggerGetInvoicePdf] = useLazyGetinvoicePdfQuery();

    const handleShareWhatsApp = async () => {
        try {
            if (!data._id) return alert("Bill ID not found!");

            const response = await triggerGetInvoicePdf(data._id).unwrap();

            const pdfUrl =
                response?.pdfUrl || `${BaseURL}invoice/print/${data._id}`;

            const customerName = data.customer?.firstName
                ? capitalize(data.customer.firstName)
                : "";

            const message = `Hi ${customerName}, here is your invoice:\n${pdfUrl}`;

            const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
            window.open(waUrl, "_blank");
        } catch (err) {
            console.error("Error sharing PDF:", err);
            alert("Failed to share invoice.");
        }
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.invoiceBox} id="print-area">
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.storeInfo}>
                        <div className={styles.storeName}>{store.storeName || "Store Name"}</div>
                        <div className={styles.storeAddress}>
                            {[
                                store.city,
                                store.address1,
                                store.address2
                            ]
                                .filter(Boolean)
                                .map((part) => capitalize(part))
                                .join(", ")}
                        </div>

                        {/* State and pincode */}
                        <div className={styles.storeAddress}>
                            {[store.state, store.pincode]
                                .filter(Boolean)
                                .map((part) => (typeof part === "string" ? capitalize(part) : part))
                                .join(" - ")}
                        </div>
                    </div>
                </div>

                <h2 className={styles.headtittle}>Invoice</h2>

                {/* Customer Info */}
                <div className={styles.infoRow}>
                    <div className={styles.left}>
                        <div>
                            <span className={styles.labelsinvoice}>Customer</span>
                            <span className={styles.labelsname}>
                                : {capitalize(user.firstName)} {capitalize(user.lastName)}
                            </span>
                        </div>
                        <div>
                            <span className={styles.labelsinvoice}>Phone</span>
                            <span className={`${styles.labels} notranslate`}>: {user.phone}</span>
                        </div>
                    </div>
                </div>

                <hr className={styles.dotted} />

                {/* Order Info */}
                <div className={styles.infoRow}>
                    <div className={styles.left}>
                        <div>
                            <span className={styles.labelsinvoice}>Invoice No</span>
                            <span className={`${styles.labels} notranslate`}>: {order.invoiceNumber}</span>
                        </div>
                        <div>
                            <span className={styles.labelsinvoice}>Date</span>
                            <span className={`${styles.labels} notranslate`}>: {formatDate(order.createdAt)}</span>
                        </div>
                        <div>
                            <span className={styles.labelsinvoice}>Time</span>
                            <span className={`${styles.labels} notranslate`}>: {formatTime(order.createdAt)}</span>
                        </div>
                        <div>
                            <span className={styles.labelsinvoice}>Status</span>
                            <span className={styles.labels}>: {capitalize(order.status)}</span>
                        </div>
                    </div>
                </div>

                <hr className={styles.dotted} />

                {/* Item Table */}
                <table className={styles.invoiceTable}>
                    <thead>
                        <tr>
                            <th className={styles.itemHeader}>Item</th>
                            <th className={styles.qtyHeader}>Quality</th>
                            <th className={styles.priceHeader}>Bag</th>
                            <th className={styles.totalHeader}>Qty</th>
                            <th className={styles.totalHeader}>Count</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length > 0 ? (
                            items.map((it, idx) => {
                                const isKilogram = it.productBag === "kilogram";
                                const isSack = it.productBag?.startsWith("sack_");

                                // format bag label
                                let bagLabel = it.productBag;
                                if (isKilogram) {
                                    bagLabel = "kg";
                                } else if (isSack) {
                                    // "sack_10kg" -> "sack10"
                                    bagLabel = it.productBag.replace("sack_", "sack").replace("kg", "");
                                }

                                return (
                                    <tr key={idx}>
                                        <td className={styles.itemCell}>{capitalize(it.productName)}</td>
                                        <td className={styles.qtyCell}>{capitalize(it.productQuality)}</td>
                                        <td className={styles.priceCell}>{bagLabel}</td>
                                        <td className={`${styles.totalCell} notranslate`}>
                                            {isKilogram ? it.productQuantity : "-"}
                                        </td>
                                        <td className={`${styles.totalCell} notranslate`}>
                                            {isSack ? it.productCount : "-"}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="5" className={styles.noItems}>No items found</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <hr className={styles.dotted} />

                {/* Summary */}
                <div className={styles.totalsinvoice}>
                    <div className={styles.totalRow}>
                        <div className={styles.fixwidthsinvoice}>
                            <p className={styles.fixwidthinvoice}>Items</p> <span> : </span>
                        </div>
                        <div className={`${styles.payable} notranslate`}>{totalItems}</div>
                    </div>
                    {/* 
                    <div className={styles.totalRow}>
                        <div className={styles.fixwidthsinvoice}>
                            <p className={styles.fixwidthinvoice}>Total Qty</p> <span> : </span>
                        </div>
                        <div className={styles.payable}>{totalQuantity}</div>
                    </div>

                    <div className={styles.totalRow}>
                        <div className={styles.fixwidthsinvoice}>
                            <p className={styles.fixwidthinvoice}>TransportAmount</p> <span> : </span>
                        </div>
                        <div className={styles.payable}>{formatCurrency(order.amount)}</div>
                    </div> */}
                    <div className={styles.totalRow}>
                        <div className={styles.fixwidthsinvoice}>
                            <p className={`${styles.fixwidthinvoice} `}>Total Qty</p> <span> : </span>
                        </div>
                        <div className={`${styles.payable} notranslate`}>{totalQuantity}</div>
                    </div>

                    <div className={styles.totalRow}>
                        <div className={styles.fixwidthsinvoice}>
                            <p className={`${styles.fixwidthinvoice} `}>TransportAmount</p> <span> : </span>
                        </div>
                        <div className={`${styles.payable} notranslate`}>{formatCurrency(order.amount)}</div>
                    </div>

                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    {mode === "thermal" && (
                        <button className={`${styles.addBtn} noPrint`} onClick={handlePrint}>
                            Print
                        </button>
                    )}

                    {mode === "whatsapp" && (
                        <button className={styles.addBtn} onClick={handleShareWhatsApp}>
                            Share on WhatsApp
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InvoicePrintDesign;
